import React from 'react';

import type { Color } from '@opendoor/tokens-studio/dist/bricks-next/types/Novo';
import { getToken } from '@tamagui/core';
import type { SvgProps } from 'react-native-svg';

type BricksSvgProps = Omit<SvgProps, 'width' | 'height' | 'color'> & {
  size?: 20 | 24;
  color?: Color;
  'aria-label'?: string;
};

// Flexible type to accept various external icon libraries (e.g., @tabler/icons-react)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExternalIconComponent = React.ComponentType<any>;

/**
 * Creates a compatibility wrapper for external icon components (e.g., from @tabler/icons-react)
 * to work with IconButton and other bricks-next components that expect the BricksSvgProps signature.
 *
 * @param IconComponent - The external icon component to wrap
 * @returns A component that matches the expected IconButton icon signature
 *
 * @example
 * ```tsx
 * import { IconBrandGoogle } from '@tabler/icons-react';
 * import { adaptExternalIcon } from '@opendoor/bricks-next';
 *
 * const GoogleIcon = adaptExternalIcon(IconBrandGoogle);
 *
 * <IconButton
 *   icon={{ Component: GoogleIcon, label: 'Sign in with Google' }}
 *   size="medium"
 * />
 * ```
 */
export function adaptExternalIcon(
  IconComponent: ExternalIconComponent,
): React.ComponentType<BricksSvgProps> {
  const AdaptedIcon = ({ size = 24, color, 'aria-label': ariaLabel, ...props }: BricksSvgProps) => {
    // Convert color token to a string value using getToken
    const tokenValue = color ? getToken(color, 'color') : undefined;
    const iconColor = tokenValue !== undefined ? String(tokenValue) : undefined;

    return <IconComponent size={size} color={iconColor} aria-label={ariaLabel} {...props} />;
  };

  AdaptedIcon.displayName = `AdaptedIcon(${
    IconComponent.displayName || IconComponent.name || 'Icon'
  })`;

  return AdaptedIcon;
}
